/**
 * @file Components/Tours/ItineraryTimeline.jsx
 * @description Vertical animated timeline for tour itineraries using Framer Motion.
 * Shows day-by-day breakdown with location markers, icons, and smooth reveal animations.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Sunrise, Mountain, Camera, Utensils, Bed, Compass, Flag, Bus, Ship } from 'lucide-react';

const iconMap = {
  MapPin, Sunrise, Mountain, Camera, Utensils, Bed, Compass, Flag, Bus, Ship,
};

const getIcon = (iconName) => {
  return iconMap[iconName] || MapPin;
};

const ItineraryTimeline = ({ itinerary = [] }) => {
  if (!itinerary || itinerary.length === 0) return null;

  const sorted = [...itinerary].sort((a, b) => a.day - b.day);

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
      <h2 className="text-2xl font-bold text-gray-900 mb-8">Day-by-Day Itinerary</h2>
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#243185] via-[#243185]/50 to-transparent" />

        {sorted.map((day, index) => {
          const Icon = getIcon(day.icon);
          const isLast = index === sorted.length - 1;

          return (
            <motion.div
              key={day._id || index}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative flex gap-6 ${!isLast ? 'pb-8' : ''}`}
            >
              {/* Timeline dot */}
              <motion.div
                className="relative z-10 flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#243185] to-[#1a2460] flex items-center justify-center shadow-lg"
                whileHover={{ scale: 1.15 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <Icon size={20} className="text-white" />
              </motion.div>

              {/* Content card */}
              <motion.div
                className="flex-1 bg-gray-50 rounded-xl p-5 hover:shadow-md transition-shadow border border-gray-100"
                whileHover={{ y: -2 }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#243185] text-white">
                    Day {day.day}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900">{day.title}</h3>
                </div>
                {day.description && (
                  <p className="text-gray-600 text-sm leading-relaxed">{day.description}</p>
                )}
                {day.location?.name && (
                  <div className="flex items-center gap-1.5 mt-3 text-xs text-[#243185] font-medium">
                    <MapPin size={13} />
                    <span>{day.location.name}</span>
                  </div>
                )}
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default ItineraryTimeline;
